import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Truck, ChevronRight, Package } from "lucide-react";
import { fmtDate, CARGA_STATUS } from "@/lib/meta";
import { toast } from "sonner";

const CargaBadge = ({ s }) => { const m = CARGA_STATUS[s] || CARGA_STATUS.AGUARDANDO_COLETA; return <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase font-mono ${m.cls}`}>{m.label}</span>; };

export default function Cargas() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isCD = ["SUPER_ADMIN", "MASTER", "GESTOR", "SUPERVISOR", "ESTOQUISTA"].includes(user.role);
  const isMotorista = user.role === "TRANSPORTE";
  const [data, setData] = useState(null);
  const [novo, setNovo] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [sel, setSel] = useState({}); // pedido_id -> volumes
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/cargas?limit=100").then((r) => setData(r.data.items || []));
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  const openNovo = async () => {
    try {
      const r = await api.get("/pedidos?status=EXPEDIDO");
      setPedidos(r.data || []); setSel({}); setNovo(true);
    } catch { toast.error("Erro ao carregar pedidos expedidos"); }
  };
  const toggle = (p) => setSel((s) => { const n = { ...s }; if (n[p.id] != null) delete n[p.id]; else n[p.id] = 1; return n; });

  const criar = async () => {
    const ids = Object.keys(sel);
    if (!ids.length) return toast.error("Selecione ao menos um pedido");
    const destinos = new Set(ids.map((id) => pedidos.find((p) => p.id === id)?.unit_id));
    if (destinos.size > 1) return toast.error("Todos os pedidos devem ter o mesmo destino");
    setSaving(true);
    try {
      for (const id of ids) {
        const q = parseInt(sel[id], 10);
        if (!q || q < 1) throw new Error("Informe a quantidade de volumes de cada pedido");
        await api.post(`/pedidos/${id}/volumes`, { quantidade: q });
      }
      const r = await api.post("/cargas", { pedido_ids: ids });
      toast.success("Carga criada");
      setNovo(false); load();
      nav(`/cargas/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erro ao criar carga"); }
    finally { setSaving(false); }
  };

  const grupos = ["AGUARDANDO_COLETA", "EM_EMBARQUE", "EM_TRANSPORTE", "CHEGADA", "EM_DESCARGA", "ENTREGUE"];

  return (
    <div className="space-y-5" data-testid="cargas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Transporte</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Truck className="w-7 h-7 text-primary" /> {isMotorista ? "Minhas Cargas" : "Cargas"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{isMotorista ? "Cargas atribuídas a você." : "Agrupe pedidos expedidos, atribua motorista e acompanhe o transporte por volumes."}</p>
        </div>
        {isCD && <Button onClick={openNovo} data-testid="btn-nova-carga"><Plus className="w-4 h-4" /> Nova Carga</Button>}
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : data.length === 0 ? (
        <Card className="bg-card/70 p-8 text-center text-muted-foreground" data-testid="cargas-vazio">Nenhuma carga.</Card>
      ) : (
        <div className="space-y-3" data-testid="cargas-lista">
          {data.filter((c) => c.status !== "CANCELADA").sort((a, b) => grupos.indexOf(a.status) - grupos.indexOf(b.status)).map((c) => (
            <Card key={c.id} className="bg-card/70 p-4 hover:border-primary/50 cursor-pointer transition-colors" onClick={() => nav(`/cargas/${c.id}`)} data-testid={`carga-${c.numero}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display text-lg font-extrabold uppercase font-mono">{c.numero}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(c.created_at)} · {c.pedido_ids?.length || 0} pedido(s)</div>
                  {c.motorista_nome && <div className="text-xs text-muted-foreground">Motorista: {c.motorista_nome}{c.veiculo_placa ? ` · ${c.veiculo_placa}` : ""}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[10px] uppercase text-muted-foreground">Volumes</div>
                    <div className="font-mono font-bold text-primary flex items-center gap-1"><Package className="w-4 h-4" /> {c.volumes_embarcados}/{c.volumes_total}</div>
                  </div>
                  <CargaBadge s={c.status} />
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={novo} onOpenChange={setNovo}>
        <DialogContent className="max-w-lg" data-testid="nova-carga-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Nova Carga</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            <p className="text-xs text-muted-foreground">Selecione pedidos EXPEDIDOS do mesmo destino e defina os volumes de cada um.</p>
            {pedidos.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">Nenhum pedido expedido disponível.</div>
            ) : pedidos.map((p) => (
              <div key={p.id} className={`flex items-center justify-between gap-2 border rounded-md p-2.5 ${sel[p.id] != null ? "border-primary/60 bg-primary/5" : "border-border"}`} data-testid={`sel-pedido-${p.numero}`}>
                <label className="flex items-center gap-2 min-w-0 cursor-pointer flex-1">
                  <input type="checkbox" checked={sel[p.id] != null} onChange={() => toggle(p)} data-testid={`chk-pedido-${p.numero}`} />
                  <div className="min-w-0"><div className="font-mono font-bold text-sm">{p.numero}</div><div className="text-[11px] text-muted-foreground truncate">Destino: {p.unit_nome || p.unit_id}</div></div>
                </label>
                {sel[p.id] != null && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Label className="text-[11px]">Volumes</Label>
                    <Input type="number" min="1" className="w-20 h-8" value={sel[p.id]} onChange={(e) => setSel((s) => ({ ...s, [p.id]: e.target.value }))} data-testid={`vol-${p.numero}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovo(false)}>Cancelar</Button>
            <Button onClick={criar} disabled={saving} data-testid="btn-criar-carga">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar Carga"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
